import { OpenFeature, ProviderEvents, ProviderStatus } from '@openfeature/react-sdk';

import { getFeatureFlagsConfig } from './config';
import type { FlagGate, FlagReadOptions } from './types';

/**
 * The primary OpenFeature domain. An OPAQUE TOKEN, not vendor coupling
 * (design D10): any provider can be registered onto it by app wiring — the
 * name is historical (Flagsmith was the first tenant). Single source of
 * truth: app registration (`onprem-bundle-provider`, `<OpenFeatureProvider>`)
 * imports this constant via the `@/features/flags/constants` shim, so the
 * domain the door reads from and the domain the app registers onto can never
 * drift apart.
 */
export const FEATURE_FLAG_DOMAIN = 'flagsmith-domain';

const clientFor = (opts?: FlagReadOptions) =>
  OpenFeature.getClient(opts?.domain ?? FEATURE_FLAG_DOMAIN);

/**
 * Synchronous boolean read for non-React code. Before the provider is ready
 * this returns `defaultValue` (OpenFeature's own no-provider behavior) — a
 * call site that makes an irreversible decision should use the gate family
 * below instead. Honors the injected `forceEnableAll` override (boolean reads
 * are the one choke point — design D8).
 */
export const readFlag = (
  key: string,
  defaultValue: boolean,
  opts?: FlagReadOptions
): boolean => {
  if (getFeatureFlagsConfig().forceEnableAll) return true;
  return clientFor(opts).getBooleanValue(key, defaultValue);
};

/**
 * Synchronous string read for non-React code. Unaffected by `forceEnableAll`
 * (there is no universal "enabled" value for string flags).
 */
export const readStringFlag = (
  key: string,
  defaultValue: string,
  opts?: FlagReadOptions
): string => clientFor(opts).getStringValue(key, defaultValue);

/**
 * Tri-state read for irreversible-decision call sites. `'unknown'` while the
 * provider has not settled to READY, so a pre-hydration read is never
 * mistaken for a real OFF (which would persist a downgrade — e.g. a lost
 * "System audio" selection).
 *
 * PLATFORM-NEUTRAL BY DESIGN: platform policy (e.g. the desktop-only
 * `!isTauri() → 'off'` short-circuit) lives in thin app-side wrappers that
 * compose it with this gate — the door never imports platform detection
 * (design D8, review round 2). Deliberately does NOT consult
 * `forceEnableAll`: the gate family is kill-switch-conservative and its
 * shipped behavior predates the injection seam — forcing `'on'` here would
 * change the parity-locked wrapper contract.
 */
export const getFlagGate = (key: string, opts?: FlagReadOptions): FlagGate => {
  const client = clientFor(opts);
  if (client.providerStatus !== ProviderStatus.READY) return 'unknown';
  return client.getBooleanValue(key, false) ? 'on' : 'off';
};

/**
 * Start-time variant: settles a pre-hydration `'unknown'` by waiting
 * (bounded) for the provider to become READY instead of treating the static
 * flag default as a real OFF. Timeout / provider ERROR resolve to `'off'`,
 * the kill-switch-conservative default.
 */
export const resolveFlagGate = async (
  key: string,
  timeoutMs = 2000,
  opts?: FlagReadOptions
): Promise<'on' | 'off'> => {
  let gate = getFlagGate(key, opts);
  if (gate === 'unknown') {
    const client = clientFor(opts);
    await new Promise<void>((resolve) => {
      const settle = () => {
        clearTimeout(timer);
        client.removeHandler(ProviderEvents.Ready, settle);
        client.removeHandler(ProviderEvents.Error, settle);
        resolve();
      };
      const timer = setTimeout(settle, timeoutMs);
      client.addHandler(ProviderEvents.Ready, settle);
      client.addHandler(ProviderEvents.Error, settle);
    });
    gate = getFlagGate(key, opts);
  }
  return gate === 'on' ? 'on' : 'off';
};
